import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Bell,
  AlertTriangle,
  AlertCircle,
  Info,
  CheckCircle,
  Trash2,
  RefreshCw,
  Settings,
  Loader2,
  X,
} from "lucide-react";
import { toast } from "sonner";

export default function AlertsPage() {
  const { api } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [thresholds, setThresholds] = useState({});
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [alertsRes, thresholdsRes] = await Promise.all([
        api.get("/alerts?limit=100"),
        api.get("/alerts/thresholds"),
      ]);
      setAlerts(alertsRes.data);
      setThresholds(thresholdsRes.data);
    } catch (error) {
      toast.error("Failed to load alerts");
    } finally {
      setLoading(false);
    }
  };

  const checkAlerts = async () => {
    setChecking(true);
    try {
      const response = await api.post("/alerts/check");
      toast.success(`Alert check complete: ${response.data.alerts_created} new alerts`);
      fetchData();
    } catch (error) {
      toast.error("Failed to check alerts");
    } finally {
      setChecking(false);
    }
  };

  const acknowledgeAlert = async (alertId) => {
    try {
      await api.post(`/alerts/${alertId}/acknowledge`);
      setAlerts(alerts.map(a => 
        a.id === alertId ? { ...a, acknowledged: true } : a
      ));
      toast.success("Alert acknowledged");
    } catch (error) {
      toast.error("Failed to acknowledge alert");
    }
  };

  const deleteAlert = async () => {
    try {
      await api.delete(`/alerts/${deleteId}`);
      setAlerts(alerts.filter(a => a.id !== deleteId));
      toast.success("Alert deleted");
    } catch (error) {
      toast.error("Failed to delete alert");
    } finally {
      setDeleteId(null);
    }
  };

  const updateThresholds = async () => {
    try {
      await api.put("/alerts/thresholds", thresholds);
      toast.success("Thresholds updated");
      setShowSettings(false);
    } catch (error) {
      toast.error("Failed to update thresholds");
    }
  };

  const getSeverityBadge = (severity) => {
    const variants = {
      critical: "bg-red-500/10 text-red-500 border-red-500/20",
      high: "bg-orange-500/10 text-orange-500 border-orange-500/20",
      medium: "bg-amber-500/10 text-amber-500 border-amber-500/20",
      low: "bg-blue-500/10 text-blue-500 border-blue-500/20",
    };
    const icons = {
      critical: AlertCircle,
      high: AlertTriangle,
      medium: Info,
      low: Info,
    };
    const Icon = icons[severity] || Info;
    
    return (
      <Badge className={`${variants[severity] || variants.low} gap-1`} variant="outline">
        <Icon className="h-3 w-3" />
        {severity}
      </Badge>
    );
  };

  const getTypeLabel = (type) => {
    const labels = {
      win_rate_drop: "Win Rate Drop",
      latency_spike: "Latency Spike",
      no_bid_rate: "High No-Bid Rate",
      revenue_drop: "Revenue Drop",
    };
    return labels[type] || type;
  };

  const unacknowledgedCount = alerts.filter(a => !a.acknowledged).length;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="alerts-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Bell className="h-8 w-8 text-accent" />
            Alerts
            {unacknowledgedCount > 0 && (
              <Badge variant="destructive" className="ml-2">
                {unacknowledgedCount} new
              </Badge>
            )}
          </h1>
          <p className="text-muted-foreground">
            Monitor bid pattern anomalies and performance issues
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setShowSettings(true)}>
            <Settings className="mr-2 h-4 w-4" />
            Thresholds
          </Button>
          <Button onClick={checkAlerts} disabled={checking}>
            {checking ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="mr-2 h-4 w-4" />
            )}
            Check Now
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-2 text-muted-foreground mb-1">
            <AlertCircle className="h-4 w-4 text-red-500" />
            <span className="text-sm">Critical</span>
          </div>
          <p className="text-2xl font-bold font-mono text-red-500">
            {alerts.filter(a => a.severity === "critical" && !a.acknowledged).length}
          </p>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 text-muted-foreground mb-1">
            <AlertTriangle className="h-4 w-4 text-orange-500" />
            <span className="text-sm">High</span>
          </div>
          <p className="text-2xl font-bold font-mono text-orange-500">
            {alerts.filter(a => a.severity === "high" && !a.acknowledged).length}
          </p>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 text-muted-foreground mb-1">
            <Info className="h-4 w-4 text-amber-500" />
            <span className="text-sm">Medium</span>
          </div>
          <p className="text-2xl font-bold font-mono text-amber-500">
            {alerts.filter(a => a.severity === "medium" && !a.acknowledged).length}
          </p>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 text-muted-foreground mb-1">
            <CheckCircle className="h-4 w-4 text-emerald-500" />
            <span className="text-sm">Acknowledged</span>
          </div>
          <p className="text-2xl font-bold font-mono text-emerald-500">
            {alerts.filter(a => a.acknowledged).length}
          </p>
        </Card>
      </div>

      {/* Alerts Table */}
      <Card>
        <CardHeader>
          <CardTitle>Alert History</CardTitle>
          <CardDescription>Recent bid pattern anomalies and notifications</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="h-8 w-8 animate-spin text-accent" />
            </div>
          ) : alerts.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Bell className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="font-medium">No alerts</p>
              <p className="text-sm">All systems are operating normally</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Time</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Severity</TableHead>
                  <TableHead>Message</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {alerts.map((alert) => (
                  <TableRow key={alert.id} className={!alert.acknowledged ? "bg-muted/30" : ""}>
                    <TableCell className="font-mono text-xs">
                      {new Date(alert.timestamp).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">{getTypeLabel(alert.type)}</Badge>
                    </TableCell>
                    <TableCell>{getSeverityBadge(alert.severity)}</TableCell>
                    <TableCell className="max-w-md">
                      <p className="text-sm truncate">{alert.message}</p>
                    </TableCell>
                    <TableCell>
                      {alert.acknowledged ? (
                        <Badge variant="outline" className="text-emerald-500">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Acknowledged
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-amber-500">
                          New
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        {!alert.acknowledged && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => acknowledgeAlert(alert.id)}
                          >
                            <CheckCircle className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setDeleteId(alert.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Settings Dialog */}
      <AlertDialog open={showSettings} onOpenChange={setShowSettings}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Alert Thresholds</AlertDialogTitle>
            <AlertDialogDescription>
              Configure when alerts should be triggered
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Win Rate Drop (%)</Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  placeholder="20"
                  value={thresholds.win_rate_drop ?? 20}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === "" || /^\d*$/.test(val)) {
                      setThresholds({ ...thresholds, win_rate_drop: val === "" ? "" : parseInt(val) });
                    }
                  }}
                  data-testid="threshold-win-rate"
                />
                <p className="text-xs text-muted-foreground">Alert when win rate drops by this %</p>
              </div>
              <div className="space-y-2">
                <Label>Latency Spike (ms)</Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  placeholder="200"
                  value={thresholds.latency_spike ?? 200}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === "" || /^\d*$/.test(val)) {
                      setThresholds({ ...thresholds, latency_spike: val === "" ? "" : parseInt(val) });
                    }
                  }}
                  data-testid="threshold-latency"
                />
                <p className="text-xs text-muted-foreground">Alert when avg latency exceeds this</p>
              </div>
              <div className="space-y-2">
                <Label>No-Bid Rate (%)</Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  placeholder="80"
                  value={thresholds.no_bid_rate ?? 80}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === "" || /^\d*$/.test(val)) {
                      setThresholds({ ...thresholds, no_bid_rate: val === "" ? "" : parseInt(val) });
                    }
                  }}
                  data-testid="threshold-nobid"
                />
                <p className="text-xs text-muted-foreground">Alert when no-bid rate exceeds this %</p>
              </div>
              <div className="space-y-2">
                <Label>Revenue Drop (%)</Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  placeholder="30"
                  value={thresholds.revenue_drop ?? 30}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === "" || /^\d*$/.test(val)) {
                      setThresholds({ ...thresholds, revenue_drop: val === "" ? "" : parseInt(val) });
                    }
                  }}
                  data-testid="threshold-revenue"
                />
                <p className="text-xs text-muted-foreground">Alert when revenue drops by this %</p>
              </div>
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={updateThresholds}>Save Changes</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Alert</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this alert?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={deleteAlert} className="bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
